import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Sparkles, LogOut, User, ChevronDown, Bell, History, Package, Sun, Moon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { OrderNotifications } from '@/components/OrderNotifications';

interface HeaderProps {
  variant?: 'default' | 'auth';
}

export const Header = ({ variant = 'default' }: HeaderProps) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [newScansCount, setNewScansCount] = useState(0);
  const [recentScans, setRecentScans] = useState<any[]>([]);
  const [ripple, setRipple] = useState<{ active: boolean; x: number; y: number; toLight: boolean }>({ 
    active: false, x: 0, y: 0, toLight: false 
  });

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Play subtle click sound
  const playToggleSound = (toLight: boolean) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different frequencies for light/dark
      oscillator.frequency.setValueAtTime(toLight ? 800 : 600, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(toLight ? 1200 : 400, audioContext.currentTime + 0.08);
      
      oscillator.type = 'sine';
      
      // Very subtle volume
      gainNode.gain.setValueAtTime(0.03, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Audio not supported, fail silently
    }
  };

  const handleThemeToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const toLight = theme === 'dark';
    
    // Play sound
    playToggleSound(toLight);
    
    setRipple({ active: true, x, y, toLight });
    
    // Change theme after a small delay to let ripple start
    setTimeout(() => {
      setTheme(toLight ? 'light' : 'dark');
    }, 50);
    
    // Remove ripple after animation
    setTimeout(() => {
      setRipple(prev => ({ ...prev, active: false }));
    }, 600);
  };

  useEffect(() => {
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      if (session) {
        loadUserProfile(session.user.id);
        loadRecentScans(session.user.id);
      } else {
        setUserProfile(null);
        setNewScansCount(0);
        setRecentScans([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      // Poll for new scans every 30 seconds
      const interval = setInterval(() => {
        loadRecentScans(userProfile.user_id);
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, userProfile]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsAuthenticated(!!session);
    if (session) {
      loadUserProfile(session.user.id);
      loadRecentScans(session.user.id);
    }
  };

  const loadUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (!error && data) {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadRecentScans = async (userId: string) => {
    try {
      // Get scans from the last 24 hours for QR codes owned by this user
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      const { data: scans, error } = await supabase
        .from('qr_scans')
        .select(`
          *,
          qr_cards:qr_card_id (
            title,
            donor_name,
            user_id
          )
        `)
        .gte('scanned_at', twentyFourHoursAgo.toISOString())
        .order('scanned_at', { ascending: false });

      if (!error && scans) {
        // IMPORTANT: Filtrer uniquement les scans des QR codes appartenant à l'utilisateur
        const userScans = scans.filter((scan: any) => scan.qr_cards?.user_id === userId);
        setRecentScans(userScans.slice(0, 5)); // Garder seulement les 5 premiers
        setNewScansCount(userScans.length);
      }
    } catch (error) {
      console.error('Error loading recent scans:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <>
      {/* Theme Ripple Effect */}
      {ripple.active && (
        <div
          className="fixed inset-0 pointer-events-none z-[9999]"
          style={{
            background: ripple.toLight 
              ? 'radial-gradient(circle at var(--ripple-x) var(--ripple-y), rgba(255,255,255,0.9) 0%, transparent 70%)'
              : 'radial-gradient(circle at var(--ripple-x) var(--ripple-y), rgba(18,18,18,0.9) 0%, transparent 70%)',
            ['--ripple-x' as any]: `${ripple.x}px`,
            ['--ripple-y' as any]: `${ripple.y}px`,
            animation: 'theme-ripple 0.6s ease-out forwards',
          }}
        />
      )}
      <style>{`
        @keyframes theme-ripple {
          0% {
            opacity: 1;
            transform: scale(0);
          }
          50% {
            opacity: 0.8;
          }
          100% {
            opacity: 0;
            transform: scale(4);
          }
        }
      `}</style>
      <header className="sticky top-0 z-50 glass-strong soft-shadow-sm border-b border-border/30">
      <div className="py-4 px-6 lg:px-8 flex items-center justify-between max-w-7xl mx-auto">
        <div 
          className="flex items-center gap-2 sm:gap-3 cursor-pointer" 
          onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
        >
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center soft-shadow">
            <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="white" />
          </div>
          <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
            QR Portrait
          </span>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Theme Toggle Button */}
          {mounted && (
            <button
              onClick={handleThemeToggle}
              className="relative w-6 h-6 rounded-full bg-secondary/80 hover:bg-secondary border border-border/50 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 overflow-hidden"
              aria-label="Toggle theme"
            >
              <div className="relative w-3 h-3">
                <Sun 
                  className={`absolute inset-0 w-3 h-3 text-amber-500 transition-all duration-500 ${
                    theme === 'dark' 
                      ? 'rotate-0 scale-100 opacity-100' 
                      : 'rotate-90 scale-0 opacity-0'
                  }`}
                />
                <Moon 
                  className={`absolute inset-0 w-3 h-3 text-primary transition-all duration-500 ${
                    theme === 'dark' 
                      ? '-rotate-90 scale-0 opacity-0' 
                      : 'rotate-0 scale-100 opacity-100'
                  }`}
                />
              </div>
            </button>
          )}
          {variant === 'auth' ? (
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="hidden sm:flex"
            >
              Retour à l'accueil
            </Button>
          ) : isAuthenticated ? (
            <>
              <OrderNotifications />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="gap-2 rounded-full px-2 py-2 h-auto hover:bg-primary/10 relative"
                >
                  <Avatar className="w-8 h-8 border-2 border-primary/20">
                    <AvatarImage src={userProfile?.avatar_url} />
                    <AvatarFallback className="bg-primary/10">
                      <User className="w-4 h-4 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                  {newScansCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs rounded-full animate-pulse"
                    >
                      {newScansCount > 9 ? '9+' : newScansCount}
                    </Badge>
                  )}
                  <ChevronDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-72 bg-background backdrop-blur-xl border-border/50 soft-shadow-lg z-[100]"
                sideOffset={8}
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {userProfile?.display_name || 'Mon compte'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {userProfile?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                
                {newScansCount > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Nouveaux scans</span>
                      </div>
                      <Badge variant="secondary" className="bg-primary/15 text-primary">
                        {newScansCount}
                      </Badge>
                    </DropdownMenuLabel>
                    <div className="max-h-48 overflow-y-auto">
                      {recentScans.map((scan: any, index: number) => (
                        <DropdownMenuItem 
                          key={scan.id}
                          className="cursor-pointer flex-col items-start gap-1 py-3 focus:bg-primary/5"
                          onClick={() => navigate('/dashboard')}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span className="text-xs font-medium text-primary">
                              {scan.qr_cards?.title || scan.qr_cards?.donor_name || 'QR Code'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(scan.scanned_at).toLocaleDateString('fr-FR', { 
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">Nouveau scan enregistré</span>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </>
                )}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer gap-2 focus:bg-primary/10 focus:text-primary"
                >
                  <User className="w-4 h-4" />
                  <span>Mon profil</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/activity')}
                  className="cursor-pointer gap-2 focus:bg-primary/10 focus:text-primary"
                >
                  <History className="w-4 h-4" />
                  <span>Activité récente</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/my-orders')}
                  className="cursor-pointer gap-2 focus:bg-primary/10 focus:text-primary"
                >
                  <Package className="w-4 h-4" />
                  <span>Mes commandes</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Déconnexion</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')} 
                className="hidden sm:flex text-sm sm:text-base"
              >
                Se connecter
              </Button>
              <Button 
                variant="premium" 
                onClick={() => navigate('/register')} 
                className="gap-1 sm:gap-2 text-sm sm:text-base px-3 sm:px-4 hidden sm:flex"
              >
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Commencer</span>
              </Button>
              {/* Mobile: Single button to auth page */}
              <Button 
                variant="premium" 
                onClick={() => navigate('/auth')} 
                className="gap-1 text-sm px-3 sm:hidden"
              >
                <Sparkles className="w-3 h-3" />
                <span>Se connecter</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
    </>
  );
};
